import {
	BASE_PRECISION_EXP,
	BigNum,
	BN,
	ContractTier,
	DriftClient,
	isVariant,
	MarketType,
	PerpMarketAccount,
	PerpMarketConfig,
	PRICE_PRECISION_EXP,
	SpotMarketConfig,
} from '@drift-labs/sdk';
import { MarketDetails24H, MarketId, UIMarket } from '@drift/common';
import { ENUM_UTILS, matchEnum } from '@drift/common';
import { SearchIcon } from '@heroicons/react/outline';
import {
	HTMLProps,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { newMarketCutoffTime } from 'src/constants/constants';
import useDisableScroll from 'src/hooks/useDisableScroll';
import useDriftActions from 'src/hooks/useDriftActions';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useFavouriteMarkets from 'src/hooks/useFavouriteMarkets';
import useLocalStorageState from 'src/hooks/useLocalStorageState';
import useWindowSizeString from 'src/hooks/useWindowSize';
import NumLib from 'src/utils/NumLib';
import {
	PERP_MARKETS_LOOKUP,
	SPOT_MARKETS_LOOKUP,
} from '../environmentVariables/EnvironmentVariables';
import useOrderedMarkets from '../hooks/useOrderedMarkets';
import useDriftStore, { DriftStore } from '../stores/DriftStore/useDriftStore';
import { PerpMarketInfo, SpotMarketInfo } from '../stores/types';
import useMarketsInfoStore from '../stores/useMarketsInfoStore';
import AnimatedToggleDrawer from './AnimatedDrawer/AnimatedToggleDrawer';
import FavouriteButton from './Buttons/FavouriteButton';
import DriftSolidIcon from './Icons/DriftSolidIcon';
import GainersIcon from './Icons/GainersIcon';
import LosersIcon from './Icons/LosersIcon';
import VolumeIcon from './Icons/VolumeIcon';
import TextField from './Inputs/TextField';
import MarketDetailStat from './MarketDetailStat';
import TargettedPopup from './Popups/TargettedPopup';
import RoundedGradientBorderBox from './RoundedGradientBorderBox';
import SkeletonValuePlaceholder from './SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import StyledSelect, { StyledSelectProps } from './StyledSelect';
import MarketIcon from './Utils/MarketIcon';
import MarketLeverageIndicator from './Utils/MarketLeverageIndicator';
import NoInsuranceWarningTooltip from './Utils/NoInsuranceWarningTooltip';
import NumberDisplay from './Utils/NumberDisplay';
import NumberDisplayV2 from './Utils/NumberDisplayV2';
import HighlightedTabs from './TabbedNavigation/HighlightedTabs';
import useMarketStateStore, {
	MarketStateStore,
} from '../stores/useMarketStateStore';
import { GraphCircle, GraphSquare } from '@drift-labs/icons';
import {
	PerpMarketSortType,
	sortOptionFromId,
	SpotMarketSortType,
	useSortMarkets,
} from 'src/hooks/useSortMarkets';
import SwitchInput from './Inputs/SwitchInput';

type MarketDisplayData = {
	symbol: string;
	baseAssetSymbol: string;
	market: UIMarket;
	price: BigNum;
	pricePercentChange: number;
	price24hAgo: number;
	volume: number;
	high: number;
	low: number;
	config: PerpMarketConfig | SpotMarketConfig;
	noInsuranceFund: boolean;
	launchTs?: number;
	openInterest?: number;
	funding?: number;
	marketAccount?: PerpMarketAccount;
};

const perpSortOptions: StyledSelectProps['options'] = [
	{ label: 'Default', value: 'default', icon: DriftSolidIcon },
	{
		label: 'Volume (desc.)',
		value: 'volume_high',
		icon: VolumeIcon,
	},
	{
		label: 'Volume (asc.)',
		value: 'volume_low',
		icon: VolumeIcon,
	},
	{
		label: 'Gainers (24hrs)',
		value: 'pricePercentChange_high',
		icon: GainersIcon,
	},
	{
		label: 'Losers (24hrs)',
		value: 'pricePercentChange_low',
		icon: LosersIcon,
	},
	{
		label: 'Funding (desc.)',
		value: 'funding_high',
		icon: GraphCircle,
	},
	{
		label: 'Funding (asc.)',
		value: 'funding_low',
		icon: GraphCircle,
	},
	{
		label: 'OI (desc.)',
		value: 'openInterest_high',
		icon: GraphSquare,
	},
	{
		label: 'OI (asc.)',
		value: 'openInterest_low',
		icon: GraphSquare,
	},
];

const spotSortOptions: StyledSelectProps['options'] = [
	{ label: 'Default', value: 'default', icon: DriftSolidIcon },
	{
		label: 'Volume (desc.)',
		value: 'volume_high',
		icon: VolumeIcon,
	},
	{
		label: 'Volume (asc.)',
		value: 'volume_low',
		icon: VolumeIcon,
	},
	{
		label: 'Gainers (24hrs)',
		value: 'pricePercentChange_high',
		icon: GainersIcon,
	},
	{
		label: 'Losers (24hrs)',
		value: 'pricePercentChange_low',
		icon: LosersIcon,
	},
];

const getPricePercentChange = (price: number, price24hAgo: number) => {
	const _price24hAgo = price24hAgo || 0;
	const markPrice = price || 0;

	const pricePercentChange =
		_price24hAgo && markPrice
			? 100 * ((markPrice - _price24hAgo) / price24hAgo)
			: 0;

	return pricePercentChange;
};

const getPerpMarketsRenderingInfo = (
	defaultOrderedPerpMarkets: {
		marketInfo: PerpMarketInfo;
		marketConfig: PerpMarketConfig;
	}[],
	driftClient: DriftClient,
	marketsData: MarketDetails24H[],
	getMarketDataForMarket: MarketStateStore['getMarketDataForMarket'],
	predictedFundings: DriftStore['predictedFundings']
) => {
	return defaultOrderedPerpMarkets.map((mktInfo) => {
		if (!mktInfo.marketInfo) {
			const marketConfig = mktInfo.marketConfig;

			// Market Info Hasn't Loaded Yet .. Return default/blank values
			return {
				symbol: marketConfig.symbol,
				baseAssetSymbol: marketConfig.baseAssetSymbol,
				market: UIMarket.createPerpMarket(marketConfig.marketIndex),
				price: null,
				pricePercentChange: null,
				price24hAgo: null,
				volume: null,
				high: null,
				low: null,
				openInterest: null,
				funding: null,
				launchTs: 0,
				config: marketConfig,
				noInsuranceFund: false,
				marketAccount: undefined,
			};
		}

		const mkt = mktInfo.marketInfo.config;
		const marketId = MarketId.createPerpMarket(mkt.marketIndex);

		const perpMarketAccount = driftClient.getPerpMarketAccount(mkt.marketIndex);
		const uiMarket = UIMarket.createPerpMarket(mkt.marketIndex);
		const market24h = marketsData.find(
			(mktData) =>
				mktData.marketIndex === uiMarket.marketIndex &&
				isVariant(mktData.marketType, 'perp')
		);
		const markPrice =
			getMarketDataForMarket(marketId)?.derivedState?.markPrice ??
			BigNum.zero(PRICE_PRECISION_EXP);
		const baseAmount = BN.max(
			perpMarketAccount.amm.baseAssetAmountLong,
			perpMarketAccount.amm.baseAssetAmountShort.abs()
		);

		const openInterest =
			baseAmount && markPrice
				? BigNum.from(baseAmount, BASE_PRECISION_EXP).mul(markPrice).toNum()
				: undefined;

		const noInsuranceFund = matchEnum(
			mktInfo.marketInfo.account.contractTier,
			ContractTier.SPECULATIVE
		);

		return {
			symbol: mkt.symbol,
			baseAssetSymbol: mkt.baseAssetSymbol,
			market: uiMarket,
			price: markPrice,
			pricePercentChange: getPricePercentChange(
				markPrice.toNum(),
				market24h?.price24hAgo
			),
			price24hAgo: market24h?.price24hAgo ?? 0,
			volume: market24h?.quoteVolume ?? 0,
			high: market24h?.priceHigh,
			low: market24h?.priceLow,
			openInterest,
			funding: parseFloat(
				predictedFundings
					.find((funding) => funding.marketIndex === mkt.marketIndex)
					?.fundingRate.toFixed(5)
			),
			launchTs: mkt.launchTs,
			config: mkt,
			noInsuranceFund,
			marketAccount: mktInfo.marketInfo.account,
		};
	});
};

const getSpotMarketsRenderingInfo = (
	defaultOrderedSpotMarkets: {
		marketInfo: SpotMarketInfo;
		marketConfig: SpotMarketConfig;
	}[],
	marketsData: MarketDetails24H[],
	getMarketDataForMarket: MarketStateStore['getMarketDataForMarket']
) => {
	return defaultOrderedSpotMarkets.map((mktInfo) => {
		if (!mktInfo.marketInfo) {
			const marketConfig =
				SPOT_MARKETS_LOOKUP[mktInfo.marketConfig.marketIndex];

			// Market Info Hasn't Loaded Yet .. Return default/blank values
			return {
				symbol: marketConfig.symbol,
				baseAssetSymbol: marketConfig.symbol,
				market: UIMarket.createSpotMarket(marketConfig.marketIndex),
				price: null,
				pricePercentChange: null,
				price24hAgo: null,
				volume: null,
				high: null,
				low: null,
				config: marketConfig,
				noInsuranceFund: false,
			};
		}

		const mkt = mktInfo.marketInfo.config;
		const uiMarket = UIMarket.createSpotMarket(mkt.marketIndex);
		const marketId = uiMarket.marketId;

		const market24h = marketsData.find(
			(mktData) =>
				mktData.marketIndex === uiMarket.market.marketIndex &&
				isVariant(mktData.marketType, 'spot')
		);
		const markPrice =
			getMarketDataForMarket(marketId)?.derivedState?.markPrice ??
			BigNum.zero(PRICE_PRECISION_EXP);

		return {
			symbol: mkt.symbol,
			baseAssetSymbol: uiMarket.baseAssetSymbol(),
			market: uiMarket,
			price: markPrice,
			pricePercentChange: getPricePercentChange(
				markPrice.toNum(),
				market24h?.price24hAgo
			),
			price24hAgo: market24h?.price24hAgo ?? 0,
			volume: market24h?.quoteVolume ?? 0,
			high: market24h?.priceHigh,
			low: market24h?.priceLow,
			config: mkt,
			noInsuranceFund: false,
		};
	});
};

const MarketBox = ({
	market,
	marketType,
	marketAccount,
	onClick,
	onFavourite,
	favourited,
	showDetails,
	fundingMultiplier,
	handleSort,
	noInsurance,
	sortedBy,
}: {
	market: MarketDisplayData;
	marketType: MarketType;
	marketAccount: PerpMarketAccount;
	onClick: () => void;
	showDetails: boolean;
	fundingMultiplier: number;
	handleSort: (sortType: string, marketType: MarketType) => void;
	noInsurance?: boolean;
	onFavourite: () => void;
	favourited: boolean;
	sortedBy: string;
}) => {
	const price24hAgo = market.price24hAgo || 0;
	const markPrice = market?.price?.toNum() || 0;

	const pricePercentChange = getPricePercentChange(markPrice, price24hAgo);

	const getMarketInfoByIndexAndType = useMarketsInfoStore(
		(s) => s.getMarketInfoByIndexAndType
	);

	const decimals =
		getMarketInfoByIndexAndType(market.config.marketIndex, marketType)
			?.genericInfo?.tickSizeExponent ?? 2;

	const fundingRateString =
		!isNaN(market.funding) && isFinite(market.funding)
			? NumLib.formatNum.toTradePrecisionString(
					market.funding * fundingMultiplier
			  )
			: null;

	return (
		<RoundedGradientBorderBox
			className="w-full border-b first:border-t border-container-border"
			borderColour="border-container-border"
			borderWidth="1px"
			borderHoverClass={`var(--app-gradient)`}
			borderRadius={`0rem`}
			style={{ flexShrink: '0' }}
		>
			<button
				className={`items-center justify-between w-full bg-container-bg`}
				onClick={onClick}
			>
				<div className="flex items-center justify-between w-full py-4 pl-2 text-text-default">
					<div className="flex items-center">
						<MarketIcon
							marketSymbol={market.market.market.symbol}
							sizeClass="w-6 h-6"
							isSpot={market.market.isSpot}
						/>

						<div className="ml-2">{market.market.marketName}</div>

						<span className="pl-1.5">
							<MarketLeverageIndicator
								marketType={market.market.marketType}
								marketIndex={market.market.market.marketIndex}
							/>
						</span>
						{market.market.isPerp && noInsurance && marketAccount && (
							<div className="ml-2">
								<NoInsuranceWarningTooltip marketInfo={marketAccount} />
							</div>
						)}
					</div>

					<div className="flex items-center justify-end pr-2 font-numeral">
						<div className="px-1 mr-1 rounded-md hover:bg-container-bg-hover">
							{market.price ? (
								<NumberDisplayV2
									value={market.price}
									displayType="notional"
									subType="$"
									toFixed={decimals}
								/>
							) : (
								<SkeletonValuePlaceholder
									className="w-16 h-[18px]"
									loading={true}
								/>
							)}
						</div>

						{price24hAgo !== 0 ? (
							<div
								className="rounded-md hover:bg-container-bg-hover text-xs min-w-[3rem]"
								onClick={(e) => {
									e.stopPropagation();
									e.preventDefault();
									handleSort(
										'pricePercentChange_high',
										market.market.marketType
									);
								}}
							>
								<NumberDisplay
									value={pricePercentChange}
									displayType="percentageChange"
									colourCoded
								/>
							</div>
						) : (
							<div className="min-w-[4rem]"></div>
						)}

						<FavouriteButton
							highlighted={favourited}
							onClick={(e) => {
								e.stopPropagation();
								e.preventDefault();
								onFavourite();
							}}
							starSizePx={20}
							wrapperSizePx={26}
							className="ml-2"
						/>
					</div>
				</div>

				{showDetails && (
					<AnimatedToggleDrawer expanded={showDetails}>
						<div className="flex items-center w-full px-2 py-2 pb-4 border-t text-text-default border-container-border">
							<MarketDetailStat
								label="24h Volume"
								value={
									market.volume != null
										? NumLib.millify(market.volume).displayString
										: null
								}
								prefix="$"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									handleSort(
										sortedBy !== 'volume_high' ? 'volume_high' : 'volume_low',
										market.market.marketType
									);
								}}
								className="flex-grow flex-2 shrink-0"
							/>

							{market.market.isPerp ? (
								<MarketDetailStat
									label="Funding"
									value={fundingRateString}
									suffix={fundingMultiplier == 1 ? '%' : '% APR'}
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										handleSort(
											sortedBy !== 'funding_high'
												? 'funding_high'
												: 'funding_low',
											market.market.marketType
										);
									}}
									className="flex-grow pl-8 flex-3 whitespace-nowrap"
								/>
							) : (
								<MarketDetailStat
									label="24h Low"
									prefix="$"
									value={
										!isNaN(market.low) && isFinite(market.low)
											? NumLib.formatNum.toTradePrecisionString(market.low)
											: null
									}
									className="flex-grow pl-8 flex-3"
								/>
							)}

							{market.market.isPerp ? (
								<MarketDetailStat
									label="Open Interest"
									value={
										market.openInterest != null
											? BigNum.from(market.openInterest).toMillified()
											: null
									}
									prefix="$"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										handleSort(
											sortedBy !== 'openInterest_high'
												? 'openInterest_high'
												: 'openInterest_low',
											market.market.marketType
										);
									}}
									className="flex-grow flex-2"
									end
								/>
							) : (
								<MarketDetailStat
									label="24h High"
									prefix="$"
									value={
										!isNaN(market.high) && isFinite(market.high)
											? NumLib.formatNum.toTradePrecisionString(market.high)
											: null
									}
									className="flex-grow flex-2"
									end
								/>
							)}
						</div>
					</AnimatedToggleDrawer>
				)}
			</button>
		</RoundedGradientBorderBox>
	);
};

const MarketSelectorDrawer = ({
	onClose,
	targetId,
}: {
	onClose: () => void;
	targetId: string;
	yPos?: number;
	width?: number;
} & HTMLProps<HTMLDivElement>) => {
	useDisableScroll();

	const windowSize = useWindowSizeString();
	const driftClient = useDriftStore((s) => s.driftClient.client);
	const driftClientIsReady = useDriftClientIsReady();
	const marketsData = useDriftStore((s) => s.marketsData24H);
	const predictedFundings = useDriftStore((s) => s.predictedFundings);
	const actions = useDriftActions();
	const switchMarket = actions.switchMarket;
	const selectedMarket = useDriftStore((s) => s.selectedMarket.current);
	const getMarketDataForMarket = useMarketStateStore(
		(s) => s.getMarketDataForMarket
	);
	const getMarketInfoByIndexAndType = useMarketsInfoStore(
		(s) => s.getMarketInfoByIndexAndType
	);
	const inputRef = useRef<HTMLInputElement>();

	const { toggleFavourite, favouriteMarketsIncludes } = useFavouriteMarkets();

	const [activeTab, setActiveTab] = useState(
		ENUM_UTILS.toStr(selectedMarket.marketType)
	);
	const [perpSortedBy, setPerpSortedBy] = useLocalStorageState(
		'marketSelectorPerpSortedBy',
		'default'
	);
	const [spotSortedBy, setSpotSortedBy] = useLocalStorageState(
		'marketSelectorSpotSortedBy',
		'default'
	);

	const [currentSearchTerm, setCurrentSearchTerm] = useState('');
	const [showDetails, setShowDetails] = useState(false);
	const [fundingRateUnit, _setFundingRateUnit] = useLocalStorageState(
		'fundingRateUnitPreference'
	);
	const fundingMultiplier = fundingRateUnit == 'y' ? 24 * 365.25 : 1;

	// Perp markets default ordering -> load rendering/display data -> sorting
	const defaultOrderedPerpMarkets = useOrderedMarkets(MarketType.PERP);
	const defaultOrderedPerpMarketsToRender = useMemo(
		() =>
			getPerpMarketsRenderingInfo(
				defaultOrderedPerpMarkets.map((mktOrderingInfo) => {
					return {
						marketInfo: getMarketInfoByIndexAndType(
							mktOrderingInfo.marketIndex,
							mktOrderingInfo.marketType
						) as PerpMarketInfo,
						marketConfig: PERP_MARKETS_LOOKUP[mktOrderingInfo.marketIndex],
					};
				}),
				driftClient,
				marketsData,
				getMarketDataForMarket,
				predictedFundings
			),
		[
			driftClientIsReady,
			marketsData,
			predictedFundings,
			defaultOrderedPerpMarkets,
		]
	);
	const perpMarketsToRender = useSortMarkets(
		defaultOrderedPerpMarketsToRender,
		sortOptionFromId(perpSortedBy),
		false
	) as MarketDisplayData[];

	// Spot markets default ordering -> load rendering/display data -> sorting
	const defaultOrderedSpotMarkets = useOrderedMarkets(MarketType.SPOT);
	const defaultOrderedSpotMarketsToRender = useMemo(() => {
		return getSpotMarketsRenderingInfo(
			defaultOrderedSpotMarkets.map((mktOrderingInfo) => {
				return {
					marketInfo: getMarketInfoByIndexAndType(
						mktOrderingInfo.marketIndex,
						mktOrderingInfo.marketType
					) as SpotMarketInfo,
					marketConfig: SPOT_MARKETS_LOOKUP[mktOrderingInfo.marketIndex],
				};
			}),
			marketsData,
			getMarketDataForMarket
		);
	}, [defaultOrderedSpotMarkets, driftClientIsReady, marketsData]);
	const spotMarketsToRender = useSortMarkets(
		defaultOrderedSpotMarketsToRender,
		sortOptionFromId(spotSortedBy),
		true
	) as MarketDisplayData[];

	const toggleShowDetails = () => {
		setShowDetails(!showDetails);
	};

	const focusInput = () => {
		inputRef?.current?.focus();
	};

	const handleSelection = (marketIndex: number, marketType: MarketType) => {
		switchMarket({ marketIndex, marketType });
		onClose();
	};

	// Hander for sorting selection changes
	const handleSortBy = useCallback(
		(sortType: string, marketType: MarketType) => {
			if (isVariant(marketType, 'perp')) {
				setPerpSortedBy(sortType);
			} else {
				setSpotSortedBy(sortType);
			}
			return;
		},
		[]
	);

	const getMaxHeightForDrawer = () => {
		// max height for the drawer is the height of the screen minus the bottom of the target box
		const targetBox = document.getElementById(targetId).getBoundingClientRect();

		const footerBox = document.getElementById('footer').getBoundingClientRect();

		const screenHeight = window.innerHeight;

		const maxHeight = screenHeight - targetBox.bottom - footerBox.height;

		return maxHeight;
	};

	const newMarkets = [...perpMarketsToRender].filter(
		(market) => market.launchTs >= newMarketCutoffTime
	);

	// Focus search input on initial render
	useEffect(() => {
		focusInput();
	}, []);

	return (
		<TargettedPopup
			targetElementId={targetId}
			onClose={onClose}
			side="bottom"
			useTargetWidth
			customPopupYOffset={() => -5}
			noBorder={windowSize === 'xs'}
			className="border-container-border sm:w-[400px]"
			customZIndex={windowSize === 'xs' ? 50 : undefined}
		>
			<div className="relative z-20 flex flex-col w-full min-h-full">
				<div
					className="flex flex-col"
					style={{ height: `${getMaxHeightForDrawer()}px` }}
				>
					<div
						className="border-b border-container-border relative"
						onClick={(e) => {
							e.stopPropagation();
						}}
					>
						<div
							className="text-text-label absolute left-3 h-full flex items-center"
							onClick={focusInput}
						>
							<SearchIcon className="w-5 h-5" />
						</div>
						<TextField.Search
							type="text"
							value={currentSearchTerm}
							onChange={(value) => {
								setCurrentSearchTerm(value);
							}}
							ref={inputRef}
							placeholder="Search Markets"
							className="w-full h-full p-4 pl-12 text-[16px]"
						/>
					</div>
					{/* Leaving the tabs here for now in case users complain and we want to add them again, but we are trying out disabling them currently (as of March '24 */}
					{false && (
						<HighlightedTabs
							currentSelection={activeTab}
							options={[
								{
									value: 'perp',
									label: 'Perpetuals',
								},
								{
									value: 'spot',
									label: `Spot`,
								},
							]}
							onChange={(selection) => {
								setActiveTab(selection);
							}}
							opts={{
								growTabs: true,
								xlPadding: true,
							}}
						/>
					)}
					<div className="flex items-center justify-start w-full px-2 py-2 border-b border-container-border">
						<div className="flex items-center">
							<SwitchInput
								label="Show Details"
								className="inline-flex"
								checked={showDetails}
								onChange={toggleShowDetails}
							/>
						</div>

						<div className="flex items-center">
							<span className="inline-flex ml-4 mr-2 text-xs font-display text-text-label ">
								Sort
							</span>
							<StyledSelect
								id="sortSelector"
								selection={activeTab == 'perp' ? perpSortedBy : spotSortedBy}
								options={
									activeTab === 'perp' ? perpSortOptions : spotSortOptions
								}
								onChange={(newValue) => {
									handleSortBy(
										newValue as PerpMarketSortType | SpotMarketSortType,
										activeTab == 'perp' ? MarketType.PERP : MarketType.SPOT
									);
								}}
								className="text-xs h-[25px]"
							/>
						</div>
					</div>
					<div className="flex flex-col overflow-auto thin-scroll">
						{perpSortedBy === 'default' &&
							currentSearchTerm.length == 0 &&
							newMarkets.filter((newMarket) =>
								isVariant(newMarket.market.marketType, activeTab)
							).length > 0 && (
								<>
									<div className="flex flex-col p-2 text-xs font-display text-text-label">
										NEW MARKETS
									</div>
									<div className="flex flex-col">
										{newMarkets
											.filter((market) =>
												market.symbol
													.toLowerCase()
													.includes(currentSearchTerm.toLowerCase())
											)
											.map((market) => (
												<MarketBox
													key={`${market.symbol}`}
													market={market}
													marketType={
														market.market.isPerp
															? MarketType.PERP
															: MarketType.SPOT
													}
													marketAccount={market.marketAccount}
													onClick={() => {
														handleSelection(
															market.market.market.marketIndex,
															MarketType.PERP
														);
													}}
													onFavourite={() => {
														toggleFavourite(market.market);
													}}
													fundingMultiplier={fundingMultiplier}
													favourited={favouriteMarketsIncludes(
														new MarketId(
															market.market.market.marketIndex,
															market.market.marketType
														)
													)}
													showDetails={showDetails}
													handleSort={handleSortBy}
													noInsurance={market.noInsuranceFund}
													sortedBy={
														market.market.isPerp ? perpSortedBy : spotSortedBy
													}
												/>
											))}
									</div>
									<div className="flex flex-col p-2 text-xs font-display text-text-label">
										ALL MARKETS
									</div>
								</>
							)}
						<div className="flex flex-col">
							{(activeTab == 'perp' ? perpMarketsToRender : spotMarketsToRender)
								.filter((market) =>
									market.symbol
										.toLowerCase()
										.includes(currentSearchTerm.toLowerCase())
								)
								.map((topMarket) => (
									<MarketBox
										key={topMarket.market.key}
										market={topMarket}
										marketAccount={topMarket.marketAccount}
										marketType={
											topMarket.market.isPerp
												? MarketType.PERP
												: MarketType.SPOT
										}
										onClick={() => {
											handleSelection(
												topMarket.market.market.marketIndex,
												topMarket.market.marketType
											);
										}}
										onFavourite={() => {
											toggleFavourite(topMarket.market);
										}}
										fundingMultiplier={fundingMultiplier}
										favourited={favouriteMarketsIncludes(
											new MarketId(
												topMarket.market.market.marketIndex,
												topMarket.market.marketType
											)
										)}
										showDetails={showDetails}
										handleSort={handleSortBy}
										noInsurance={topMarket.noInsuranceFund}
										sortedBy={
											topMarket.market.isPerp ? perpSortedBy : spotSortedBy
										}
									/>
								))}
						</div>
					</div>
				</div>
			</div>
		</TargettedPopup>
	);
};

export default MarketSelectorDrawer;
